"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader, FileText, CircleCheck, CircleAlert } from "lucide-react";
const formSpec = {
  "form_key": "consultation_inquiry_form_contact_us",
  "form_label": "Request a Consultation",
  "fields": [{
    "key": "name",
    "label": "Full Name",
    "type": "text",
    "required": true,
    "placeholder": "e.g. John Smith",
    "help_text": "Please enter your full legal name.",
    "options": null
  }, {
    "key": "company",
    "label": "Organization",
    "type": "text",
    "required": true,
    "placeholder": "e.g. Abu Dhabi Enterprise",
    "help_text": "The entity you are representing.",
    "options": null
  }, {
    "key": "email",
    "label": "Corporate Email",
    "type": "email",
    "required": true,
    "placeholder": "john.smith@company.ae",
    "help_text": "We will send our response to this address.",
    "options": null
  }, {
    "key": "phone",
    "label": "Contact Number",
    "type": "tel",
    "required": false,
    "placeholder": "+971 50 000 0000",
    "help_text": "Optional - for direct coordination.",
    "options": null
  }, {
    "key": "services_needed",
    "label": "Areas of Interest",
    "type": "checkbox",
    "required": true,
    "help_text": "Select the areas you would like to discuss.",
    "options": ["Cybersecurity & GRC", "API Security & Management", "Security Operations & Threat Intelligence", "Infrastructure & Cloud Modernization", "Digital Transformation", "AI Governance & Security", "Enterprise Applications", "Other Requirements"]
  }, {
    "key": "project_summary",
    "label": "Inquiry Details",
    "type": "textarea",
    "required": true,
    "placeholder": "Briefly describe your requirements, objectives or current challenges.",
    "help_text": "Please do not include passwords, credentials, confidential data or sensitive operational information in this form.",
    "options": null
  }, {
    "key": "consent",
    "label": "I consent to FETC using my submitted information to respond to this inquiry.",
    "type": "checkbox",
    "required": true,
    "help_text": null,
    "options": null
  }]
};
function ProposalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-secondary border border-border rounded-lg shadow-md">
        <CircleCheck className="size-16 text-primary mb-6" />
        <h3 className="text-2xl font-bold font-default mb-2 text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono uppercase tracking-wider text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-primary text-primary-foreground size-8 flex items-center justify-center rounded-md font-mono text-sm font-bold">01</div>
          <h3 className="text-lg font-bold font-default uppercase tracking-wide text-foreground">Contact Information</h3>
          <Separator className="flex-1 bg-border" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Full Name *</FormLabel>
                <FormControl>
                  <Input placeholder={formSpec.fields[0].placeholder || ""} {...field} className="font-default bg-secondary border-border focus-visible:ring-ring rounded-md" />
                </FormControl>
                <FormDescription className="text-xs text-muted-foreground/70">{formSpec.fields[0].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Organization *</FormLabel>
                <FormControl>
                  <Input placeholder={formSpec.fields[1].placeholder || ""} {...field} className="font-default bg-secondary border-border focus-visible:ring-ring rounded-md" />
                </FormControl>
                <FormDescription className="text-xs text-muted-foreground/70">{formSpec.fields[1].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Corporate Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={formSpec.fields[2].placeholder || ""} {...field} className="font-default bg-secondary border-border focus-visible:ring-ring rounded-md" />
                </FormControl>
                <FormDescription className="text-xs text-muted-foreground/70">{formSpec.fields[2].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Contact Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder={formSpec.fields[3].placeholder || ""} {...field} className="font-default bg-secondary border-border focus-visible:ring-ring rounded-md" />
                </FormControl>
                <FormDescription className="text-xs text-muted-foreground/70">{formSpec.fields[3].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-primary/20 text-primary size-8 flex items-center justify-center rounded-md font-mono text-sm font-bold">02</div>
          <h3 className="text-lg font-bold font-default tracking-wide text-white">Consultation Scope</h3>
          <Separator className="flex-1 bg-border" />
        </div>

        <FormField control={control} name="services_needed" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Areas of Interest *</FormLabel>
              <FormDescription className="text-xs mb-3 text-muted-foreground/70">{formSpec.fields[4].help_text}</FormDescription>
              <FormControl>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {formSpec.fields[4].options?.map((option, i) => <label key={option} data-index={i} className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-all duration-300 ${field.value?.includes(option) ? 'bg-accent/10 border-ring' : 'bg-secondary border-border hover:bg-muted'}`}>
                      <input type="checkbox" className="mt-1 size-4 rounded-sm border-primary text-primary focus:ring-ring" checked={field.value?.includes(option) || false} onChange={e => {
                const current = field.value || [];
                const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
                field.onChange(updated);
              }} />
                      <span className="text-sm font-medium leading-none text-foreground">
                        {option}
                      </span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_summary" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Inquiry Details *</FormLabel>
              <FormControl>
                <textarea className="flex min-h-[160px] w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm shadow-sm transition-all duration-300 outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 font-default resize-y" placeholder={formSpec.fields[5].placeholder || ""} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">{formSpec.fields[5].help_text}</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4 bg-secondary/50">
              <FormControl>
                <input type="checkbox" className="mt-1 size-4 rounded-sm border-primary text-primary focus:ring-ring" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-medium text-foreground cursor-pointer">
                  {formSpec.fields[6].label} *
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <div className="pt-6 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] font-default font-semibold uppercase tracking-wider bg-[#0F62FE] text-white hover:bg-[#0043CE] shadow-md transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>
              <FileText className="mr-2 size-4" />
              Request a Consultation
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ConsultationInquiry() {
  return <section data-section-id="224947" id="consultation-inquiry" className="py-20 md:py-32 bg-background text-foreground relative">
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{
      backgroundImage: 'linear-gradient(var(--color-primary) 1px, transparent 1px), linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }}>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
        <div className="mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg text-white text-xs font-mono font-bold uppercase tracking-widest mb-4" style={{
          backgroundColor: '#13B8C8'
        }}>
            <span className="size-2 rounded-lg animate-pulse" style={{
            backgroundColor: '#13B8C8',
            filter: 'brightness(1.5)'
          }}></span>
            Expert Consultation
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight text-foreground mb-4">
            Request a Consultation
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl font-default leading-relaxed">
            Connect with FETC experts to discuss your cybersecurity and technology requirements. Our team provides secure, scalable, and practical solutions tailored for UAE government and enterprise standards.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
          <div className="bg-secondary border-b border-border px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FileText className="size-5 text-primary" />
              <span className="font-mono text-sm font-semibold text-foreground uppercase tracking-wider">Inquiry Form</span>
            </div>
          </div>

          <div className="p-6 md:p-10">
            <FormProvider formKey="consultation_inquiry_form_contact_us" formId={formSpec.form_key} sectionName="RequestATechnicalProposal" recipientEmail="ehab.hashiem@fetc.ae" defaultValues={{
            name: "",
            company: "",
            email: "",
            phone: "",
            services_needed: [],
            project_summary: "",
            consent: false
          }} validationRules={{
            name: {
              required: "Full name is required"
            },
            company: {
              required: "Organization name is required"
            },
            email: {
              required: "Corporate email address is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address format"
              }
            },
            services_needed: {
              required: "Please select at least one area of interest",
              custom: (val: string[]) => val && val.length > 0 ? null : "Please select at least one area of interest"
            },
            project_summary: {
              required: "Inquiry details are required",
              minLength: {
                value: 20,
                message: "Please provide more detail (minimum 20 characters)"
              }
            },
            consent: {
              required: "You must consent to proceed",
              custom: (val: boolean) => val === true ? null : "You must consent to proceed"
            }
          }} submitText="Request a Consultation" successMessage="Your inquiry has been successfully logged. A FETC technical consultant will review your requirements and contact you shortly to schedule a formal discussion.">
              <ProposalForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}